import React, { useState, useEffect } from 'react';
import { Plus, Search, Edit, Trash2, Eye, Package, AlertTriangle, Info, ChevronUp, ChevronDown, Filter } from '../components/Icons';
import { useAuth } from '../contexts/AuthContext';
import Navigation from '../components/Navigation';
import SearchableDropdown from '../components/SearchableDropdown';
import { apiGet, apiPost, apiPut, apiDelete } from '../utils/api';
import toast from 'react-hot-toast';

const Products = () => {
  const { user } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalProducts, setTotalProducts] = useState(0);
  const [categories, setCategories] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [stockFilter, setStockFilter] = useState('');
  const [saleTypeFilter, setSaleTypeFilter] = useState('');
  const [provinceFilter, setProvinceFilter] = useState('');
  const [taxRateFilter, setTaxRateFilter] = useState('');
  const [sroScheduleFilter, setSroScheduleFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [sortField, setSortField] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  
  // Dynamic filter values from backend
  const [filterValues, setFilterValues] = useState({
    sale_types: [],
    tax_rates: [],
    provinces: [],
    sro_schedules: []
  });
  
  // Helper function to format date for FBR APIs (DD-MonthNameYYYY format)
  const formatDateForFbr = (date) => {
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 
                   'July', 'August', 'September', 'October', 'November', 'December'];
    const day = date.getDate().toString().padStart(2, '0');
    const month = months[date.getMonth()];
    const year = date.getFullYear();
    return `${day}-${month}${year}`;
  };

  // FBR data states
  const [fbrData, setFbrData] = useState({
    uomList: [],
    // Every unit FBR publishes. uomList gets narrowed to the units valid for the
    // chosen HS code, which is right for the primary unit but wrong for the
    // secondary one: that never reaches FBR, so it must not inherit the filter.
    allUomList: [],
    hsCodes: [],
    provinces: [],
    transactionTypes: [],
    taxRates: [],
    sroSchedules: [],
    sroItems: []
  });
  const [loadingFbrData, setLoadingFbrData] = useState(false);
  // Secondary units this tenant already uses that are not in FBR's list.
  const [customUoms, setCustomUoms] = useState([]);
  
  // Cache for FBR API responses to avoid repeated calls
  const [fbrCache, setFbrCache] = useState({
    taxRates: new Map(),
    sroSchedules: new Map(),
    sroItems: new Map()
  });

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    sku: '',
    barcode: '',
    unit_price: '',
    stock_quantity: '100',
    min_stock_level: '50',
    category_id: '',
    // FBR-specific fields
    hs_code: '',
    product_description: '',
    rate: '18%',
    uom: 'Numbers, pieces, units',
    sale_type: 'Goods at standard rate (default)',
    sro_schedule: '',
    fixed_notified_value: '',
    sales_tax_withheld: '',
    extra_tax: '',
    further_tax: '',
    fed_payable: '',
    // FBR hybrid fields (ID + description)
    sale_type_id: '',
    sale_type_desc: '',
    rate_id: '',
    rate_value: '',
    uom_id: '',
    uom_desc: '',
    secondary_uom: '',
    conversion_factor: '',
    sro_schedule_id: '',
    sro_item_id: '',
    province_id: '',
    province_desc: '',
    origination_supplier: '' // Legacy field for province
  });

  useEffect(() => {
    fetchFilterValues();
    fetchCustomUoms();
  }, []);

  useEffect(() => {
    fetchProducts();
    fetchCategories();
    fetchFbrData();
    setSelectedProducts([]);
    setSelectAll(false);
  }, [currentPage, searchTerm, categoryFilter, stockFilter, saleTypeFilter, provinceFilter, taxRateFilter, sroScheduleFilter, dateFrom, dateTo, sortField, sortDirection, itemsPerPage]);

  const fetchFilterValues = async () => {
    try {
      const response = await apiGet('/api/inventory/filter-values');
      if (response.ok) {
        setFilterValues(response.data);
      }
    } catch (error) {
      console.error('Error fetching filter values:', error);
    }
  };

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const params = {
        skip: (currentPage - 1) * itemsPerPage,
        limit: itemsPerPage,
        sort_by: sortField,
        sort_order: sortDirection
      };

      if (searchTerm) {
        params.search = searchTerm;
      }
      if (categoryFilter) {
        params.category_id = categoryFilter;
      }
      if (stockFilter) {
        params.stock_level = stockFilter; // Fixed: was stock_filter
      }
      if (saleTypeFilter) {
        params.sale_type = saleTypeFilter;
      }
      if (provinceFilter) {
        params.province = provinceFilter;
      }
      if (taxRateFilter) {
        params.tax_rate = taxRateFilter;
      }
      if (sroScheduleFilter) {
        params.sro_schedule = sroScheduleFilter;
      }
      if (dateFrom) {
        params.date_from = dateFrom;
      }
      if (dateTo) {
        params.date_to = dateTo;
      }

      const response = await apiGet('/api/inventory/products', params);

      if (response.ok) {
        setProducts(response.data.products || []);
        setTotalProducts(response.data.total || 0);
        setTotalPages(Math.ceil((response.data.total || 0) / itemsPerPage));
      } else {
        console.error('Failed to fetch products:', response.data);
        setProducts([]);
        toast.error('Failed to fetch products');
      }
    } catch (error) {
      console.error('Error fetching products:', error);
      setProducts([]);
      toast.error('Error fetching products');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await apiGet('/api/inventory/categories/');
      if (response.ok) {
        setCategories(response.data.categories || []);
      } else {
        console.error('Error fetching categories:', response.data);
        toast.error('Failed to fetch categories');
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      toast.error('Failed to fetch categories');
    }
  };

  const fetchFbrData = async () => {
    try {
      setLoadingFbrData(true);
      console.log('🔄 Fetching FBR data...');
      
      // Check if user is authenticated
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('❌ No authentication token found. Please login first.');
        setLoadingFbrData(false);
        return;
      }
      
      // Fetch only essential FBR data in parallel (no test endpoint, no document-types, no sro-items)
      const [uomResponse, hsCodesResponse, provincesResponse, transactionTypesResponse] = await Promise.all([
        apiGet('/api/fbr/uom'),
        apiGet('/api/fbr/hs-codes'),
        apiGet('/api/fbr/provinces'),
        apiGet('/api/fbr/transaction-types')
      ]);

      console.log('📊 FBR API Responses:', {
        uom: uomResponse.status,
        hsCodes: hsCodesResponse.status,
        provinces: provincesResponse.status,
        transactionTypes: transactionTypesResponse.status
      });

      // Check for authentication errors
      const authErrors = [uomResponse, hsCodesResponse, provincesResponse, transactionTypesResponse]
        .filter(response => response.status === 401 || response.status === 403);
      
      if (authErrors.length > 0) {
        console.error('❌ Authentication failed for FBR endpoints. Status codes:', 
          authErrors.map(r => r.status));
        console.error('Please check if you are logged in and the token is valid.');
        setLoadingFbrData(false);
        return;
      }

      setFbrData(prev => ({
        ...prev,
        uomList: uomResponse.ok ? uomResponse.data || [] : [],
        allUomList: uomResponse.ok ? uomResponse.data || [] : [],
        hsCodes: hsCodesResponse.ok ? hsCodesResponse.data || [] : [],
        provinces: provincesResponse.ok ? provincesResponse.data || [] : [],
        transactionTypes: transactionTypesResponse.ok ? transactionTypesResponse.data || [] : []
      }));

      console.log('✅ FBR data loaded successfully');

    } catch (error) {
      console.error('❌ Error fetching FBR data:', error);
    } finally {
      setLoadingFbrData(false);
    }
  };

  const fetchTaxRates = async (transactionTypeId, originationSupplier) => {
    try {
      const response = await apiGet(`/api/fbr/tax-rates-for-sale-type?date=2024-01-01&trans_type_id=${transactionTypeId}&origination_supplier=${originationSupplier}`);
      if (response.ok) {
        setFbrData(prev => ({
          ...prev,
          taxRates: response.data || []
        }));
      } else {
        console.error('Error fetching tax rates:', response.data);
        toast.error('Failed to fetch tax rates');
      }
    } catch (error) {
      console.error('Error fetching tax rates:', error);
      toast.error('Failed to fetch tax rates');
    }
  };

  const fetchSroSchedules = async (rateId, originationSupplier) => {
    try {
      const response = await apiGet(`/api/fbr/sro-schedules-for-rate?rate_id=${rateId}&date=2024-01-01&origination_supplier=${originationSupplier}`);
      if (response.ok) {
        setFbrData(prev => ({
          ...prev,
          sroSchedules: response.data || []
        }));
      } else {
        console.error('Error fetching SRO schedules:', response.data);
        toast.error('Failed to fetch SRO schedules');
      }
    } catch (error) {
      console.error('Error fetching SRO schedules:', error);
      toast.error('Failed to fetch SRO schedules');
    }
  };

  const fetchSroItems = async (sroId) => {
    try {
      const response = await apiGet(`/api/fbr/sro-items-for-schedule?sro_id=${sroId}&date=2024-01-01`);
      if (response.ok) {
        setFbrData(prev => ({
          ...prev,
          sroItems: response.data || []
        }));
      } else {
        console.error('Error fetching SRO items:', response.data);
        toast.error('Failed to fetch SRO items');
      }
    } catch (error) {
      console.error('Error fetching SRO items:', error);
      toast.error('Failed to fetch SRO items');
    }
  };


  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) {
      return <ChevronUp className="h-4 w-4 text-slate-400" />;
    }
    return sortDirection === 'asc' ? 
      <ChevronUp className="h-4 w-4 text-amber-600" /> :
      <ChevronDown className="h-4 w-4 text-amber-600" />;
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setSearchInput('');
    setCategoryFilter('');
    setStockFilter('');
    setSaleTypeFilter('');
    setProvinceFilter('');
    setTaxRateFilter('');
    setSroScheduleFilter('');
    setDateFrom('');
    setDateTo('');
    setCurrentPage(1);
  };

  const handleSearch = (e) => {
    if (e) e.preventDefault();
    setSearchTerm(searchInput);
    setCurrentPage(1);
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // FBR field change handlers
  const handleHsCodeChange = (hsCode) => {
    setFormData(prev => ({ ...prev, hs_code: hsCode }));
    // Auto-populate UOM for selected HS code
    if (hsCode) {
      fetchUomForHsCode(hsCode);
    }
  };

  const handleTransactionTypeChange = (transactionTypeId) => {
    // Find the selected transaction type to get both ID and description
    const selectedType = fbrData.transactionTypes.find(type => type.id === transactionTypeId);
    setFormData(prev => {
      const newFormData = { 
        ...prev, 
        sale_type: selectedType?.description || transactionTypeId,
        sale_type_id: transactionTypeId,
        sale_type_desc: selectedType?.description || ''
      };
      
      // Auto-populate tax rates using the updated formData
      if (transactionTypeId && newFormData.origination_supplier) {
        const today = formatDateForFbr(new Date());
        fetchTaxRatesForSaleType(today, transactionTypeId, newFormData.origination_supplier);
      }
      
      return newFormData;
    });
  };

  const handleProvinceChange = (provinceId) => {
    // Find the selected province to get both ID and description
    const selectedProvince = fbrData.provinces.find(province => province.id === provinceId);
    setFormData(prev => {
      const newFormData = { 
        ...prev, 
        origination_supplier: provinceId, // Legacy field
        province_id: provinceId,
        province_desc: selectedProvince?.description || ''
      };
      
      // Auto-populate tax rates using the updated formData
      if (newFormData.sale_type_id && provinceId) {
        const today = formatDateForFbr(new Date());
        fetchTaxRatesForSaleType(today, newFormData.sale_type_id, provinceId);
      }
      
      return newFormData;
    });
  };

  const handleTaxRateChange = (rateId) => {
    // Find the selected tax rate to get both ID and description
    const selectedRate = fbrData.taxRates.find(rate => rate.id === rateId);
    setFormData(prev => {
      const newFormData = { 
        ...prev, 
        rate: selectedRate?.description || rateId,
        rate_id: rateId,
        rate_value: selectedRate?.value || 0,
        rate_desc: selectedRate?.description || ''
      };
      
      // Auto-populate SRO schedules using the updated formData
      if (rateId && newFormData.origination_supplier) {
        const today = formatDateForFbr(new Date());
        fetchSroSchedulesForRate(rateId, today, newFormData.origination_supplier);
      }
      
      return newFormData;
    });
  };

  const handleSroScheduleChange = (sroId) => {
    // Find the selected SRO schedule to get both ID and description
    const selectedSchedule = fbrData.sroSchedules.find(schedule => schedule.id === sroId);
    setFormData(prev => {
      const newFormData = { 
        ...prev, 
        sro_schedule: selectedSchedule?.description || sroId,
        sro_schedule_id: sroId,
        sro_item_serial_no: '', // Clear SRO item when schedule changes
        sro_item_id: '' // Also clear SRO item ID
      };
      
      // Auto-populate SRO items
      if (sroId) {
        const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format
        fetchSroItemsForSchedule(today, sroId);
      } else {
        // Clear SRO items when no schedule is selected
        setFbrData(prev => ({
          ...prev,
          sroItems: []
        }));
      }
      
      return newFormData;
    });
  };

  // Units the tenant has already used as a secondary unit, so a unit typed once
  // is there for everyone next time rather than having to be retyped.
  // The secondary unit is never sent to FBR, so it offers every unit FBR
  // publishes plus anything this tenant has added, whatever HS code is chosen.
  const secondaryUomOptions = (() => {
    const seen = new Map();
    const add = (name) => {
      const label = String(name || '').trim();
      if (!label || seen.has(label.toLowerCase())) return;
      seen.set(label.toLowerCase(), { id: label, description: label });
    };
    (fbrData.allUomList || []).forEach(uom => add(uom.description));
    customUoms.forEach(add);
    // Whatever this product already carries, even if it came from an import.
    add(formData.secondary_uom);
    const sorted = [...seen.values()].sort((a, b) =>
      a.description.localeCompare(b.description, undefined, { sensitivity: 'base' }));
    return [{ id: '', description: 'None' }, ...sorted];
  })();

  const fetchCustomUoms = async () => {
    try {
      const response = await apiGet('/api/inventory/secondary-uoms');
      if (response.ok) setCustomUoms(response.data?.units || []);
    } catch (error) {
      console.error('Error fetching secondary units:', error);
    }
  };

  const fetchUomForHsCode = async (hsCode) => {
    try {
      const response = await apiGet(`/api/fbr/uom-for-hs-code?hs_code=${hsCode}`);
      if (response.ok) {
        setFbrData(prev => ({
          ...prev,
          uomList: response.data || []
        }));
        // Auto-select the first UOM if only one is returned
        if (response.data && response.data.length === 1) {
          setFormData(prev => ({
            ...prev,
            uom: response.data[0].description
          }));
        }
      }
    } catch (error) {
      console.error('Error fetching UOM for HS code:', error);
    }
  };

  const fetchTaxRatesForSaleType = async (date, transTypeId, originationSupplier) => {
    if (!transTypeId || !originationSupplier) return;
    
    // Check cache first
    const cacheKey = `${transTypeId}-${originationSupplier}`;
    if (fbrCache.taxRates.has(cacheKey)) {
      console.log('📦 Using cached tax rates for:', cacheKey);
      setFbrData(prev => ({
        ...prev,
        taxRates: fbrCache.taxRates.get(cacheKey)
      }));
      return;
    }
    
    try {
      const response = await apiGet(`/api/fbr/tax-rates-for-sale-type?date=${date}&trans_type_id=${transTypeId}&origination_supplier=${originationSupplier}`);
      if (response.ok) {
        const data = response.data || [];
        setFbrData(prev => ({
          ...prev,
          taxRates: data
        }));
        
        // Cache the result
        setFbrCache(prev => ({
          ...prev,
          taxRates: new Map(prev.taxRates).set(cacheKey, data)
        }));
      }
    } catch (error) {
      console.error('Error fetching tax rates:', error);
    }
  };

  const fetchSroSchedulesForRate = async (rateId, date, originationSupplier) => {
    if (!rateId || !originationSupplier) return;
    
    // Check cache first
    const cacheKey = `${rateId}-${originationSupplier}`;
    if (fbrCache.sroSchedules.has(cacheKey)) {
      console.log('📦 Using cached SRO schedules for:', cacheKey);
      setFbrData(prev => ({
        ...prev,
        sroSchedules: fbrCache.sroSchedules.get(cacheKey)
      }));
      return;
    }
    
    try {
      const response = await apiGet(`/api/fbr/sro-schedules-for-rate?rate_id=${rateId}&date=${date}&origination_supplier=${originationSupplier}`);
      if (response.ok) {
        const data = response.data || [];
        setFbrData(prev => ({
          ...prev,
          sroSchedules: data
        }));
        
        // Cache the result
        setFbrCache(prev => ({
          ...prev,
          sroSchedules: new Map(prev.sroSchedules).set(cacheKey, data)
        }));
      }
    } catch (error) {
      console.error('Error fetching SRO schedules:', error);
    }
  };

  const fetchSroItemsForSchedule = async (date, sroId) => {
    if (!sroId) return;
    
    // Check cache first
    if (fbrCache.sroItems.has(sroId)) {
      console.log('📦 Using cached SRO items for:', sroId);
      setFbrData(prev => ({
        ...prev,
        sroItems: fbrCache.sroItems.get(sroId)
      }));
      return;
    }
    
    try {
      const response = await apiGet(`/api/fbr/sro-items-for-schedule?date=${date}&sro_id=${sroId}`);
      if (response.ok) {
        const data = response.data || [];
        setFbrData(prev => ({
          ...prev,
          sroItems: data
        }));
        
        // Cache the result
        setFbrCache(prev => ({
          ...prev,
          sroItems: new Map(prev.sroItems).set(sroId, data)
        }));
      }
    } catch (error) {
      console.error('Error fetching SRO items:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Convert string values to proper types for backend
      const productData = {
        name: formData.name,
        description: formData.description,
        sku: formData.sku || undefined,
        barcode: formData.barcode || undefined,
        unit_price: formData.unit_price !== '' ? parseFloat(formData.unit_price) : undefined,
        stock_quantity: parseInt(formData.stock_quantity),
        min_stock_level: parseInt(formData.min_stock_level) || 10,
        category_id: formData.category_id ? parseInt(formData.category_id) : undefined,
        // FBR-specific fields
        hs_code: formData.hs_code || undefined,
        product_description: formData.product_description || undefined,
        rate: formData.rate ? String(formData.rate) : undefined,
        uom: formData.uom ? String(formData.uom) : undefined,
        sale_type: formData.sale_type ? String(formData.sale_type) : undefined, // Legacy field
        sro_schedule_no: formData.sro_schedule ? String(formData.sro_schedule) : undefined, // Legacy field
        sro_item_serial_no: formData.sro_item_serial_no ? String(formData.sro_item_serial_no) : undefined,
        fixed_notified_value: formData.fixed_notified_value ? parseFloat(formData.fixed_notified_value) : undefined,
        sales_tax_withheld: formData.sales_tax_withheld ? parseFloat(formData.sales_tax_withheld) : undefined,
        extra_tax: formData.extra_tax ? parseFloat(formData.extra_tax) : undefined,
        further_tax: formData.further_tax ? parseFloat(formData.further_tax) : undefined,
        fed_payable: formData.fed_payable ? parseFloat(formData.fed_payable) : undefined,
        // FBR hybrid fields (ID + description)
        sale_type_id: formData.sale_type_id ? parseInt(formData.sale_type_id) : undefined,
        sale_type_desc: formData.sale_type_desc || undefined,
        rate_id: formData.rate_id ? parseInt(formData.rate_id) : undefined,
        rate_value: formData.rate_value ? parseFloat(formData.rate_value) : undefined,
        uom_id: formData.uom_id ? parseInt(formData.uom_id) : undefined,
        uom_desc: formData.uom_desc || undefined,
        secondary_uom: formData.secondary_uom || undefined,
        conversion_factor: formData.secondary_uom && formData.conversion_factor ? parseFloat(formData.conversion_factor) : undefined,
        sro_schedule_id: formData.sro_schedule_id ? parseInt(formData.sro_schedule_id) : undefined,
        sro_item_id: formData.sro_item_id ? parseInt(formData.sro_item_id) : undefined,
        province_id: formData.province_id ? parseInt(formData.province_id) : undefined,
        province_desc: formData.province_desc || undefined
      };

      let response;
      if (editingProduct) {
        response = await apiPut(`/api/inventory/product/${editingProduct.id}`, productData);
      } else {
        response = await apiPost('/api/inventory/products', productData);
      }

      if (response.ok) {
        toast.success(editingProduct ? 'Product updated successfully!' : 'Product created successfully!');
        setShowModal(false);
        setEditingProduct(null);
        resetForm();
        fetchProducts();
      } else {
        const errorMessage = response.data?.detail || response.data?.message || 'Error saving product';
        toast.error(`Error: ${errorMessage}`);
      }
    } catch (error) {
      console.error('Error saving product:', error);
      toast.error('Error saving product');
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name,
      description: product.description,
      sku: product.sku,
      barcode: product.barcode,
      unit_price: product.unit_price?.toString() || '',
      stock_quantity: product.stock_quantity?.toString() || '100',
      min_stock_level: product.min_stock_level?.toString() || '50',
      category_id: product.category_id?.toString() || '',
      // FBR-specific fields
      hs_code: product.hs_code || '',
      product_description: product.product_description || '',
      rate: product.rate || '18%',
      uom: product.uom || 'Numbers, pieces, units',
      sale_type: product.sale_type || 'Goods at standard rate (default)',
      sro_schedule: product.sro_schedule_no || '',
      sro_item_serial_no: product.sro_item_serial_no || '',
      fixed_notified_value: product.fixed_notified_value?.toString() || '',
      sales_tax_withheld: product.sales_tax_withheld?.toString() || '',
      extra_tax: product.extra_tax?.toString() || '',
      further_tax: product.further_tax?.toString() || '',
      fed_payable: product.fed_payable?.toString() || '',
      // FBR hybrid fields (ID + description)
      sale_type_id: product.sale_type_id?.toString() || '',
      sale_type_desc: product.sale_type_desc || '',
      rate_id: product.rate_id?.toString() || '',
      rate_value: product.rate_value?.toString() || '',
      uom_id: product.uom_id?.toString() || '',
      uom_desc: product.uom_desc || '',
      secondary_uom: product.secondary_uom || '',
      conversion_factor: product.conversion_factor?.toString() || '',
      sro_schedule_id: product.sro_schedule_id?.toString() || '',
      sro_item_id: product.sro_item_id?.toString() || '',
      province_id: product.province_id?.toString() || '',
      province_desc: product.province_desc || '',
      origination_supplier: product.province_id?.toString() || '' // Legacy field
    });
    setShowModal(true);
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Are you sure you want to deactivate this product? It will be hidden from the products list but can be reactivated later.')) return;

    try {
      console.log('🗑️ Attempting to deactivate product with ID:', productId);
      const response = await apiDelete(`/api/inventory/product/${productId}`);
      console.log('🗑️ Deactivate response:', response);

      if (response.ok) {
        toast.success('Product deactivated successfully! It has been hidden from the products list.');
        fetchProducts();
      } else {
        const errorMessage = response.data?.detail || response.data?.message || 'Failed to deactivate product';
        console.error('🗑️ Deactivate failed:', errorMessage);
        toast.error(`Failed to deactivate product: ${errorMessage}`);
      }
    } catch (error) {
      console.error('🗑️ Error deactivating product:', error);
      toast.error('Error deactivating product');
    }
  };

  const handleSelectProduct = (productId) => {
    if (selectedProducts.includes(productId)) {
      setSelectedProducts(selectedProducts.filter(id => id !== productId));
      setSelectAll(false);
    } else {
      setSelectedProducts([...selectedProducts, productId]);
    }
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedProducts([]);
    } else {
      setSelectedProducts(products.map(product => product.id));
    }
    setSelectAll(!selectAll);
  };

  const bulkDeactivateProducts = async () => {
    if (selectedProducts.length === 0) {
      toast.error('Please select products to deactivate');
      return;
    }
    if (!window.confirm(`Are you sure you want to deactivate ${selectedProducts.length} product(s)?`)) return;
    
    try {
      const response = await apiPost('/api/inventory/bulk-deactivate', selectedProducts);
      if (response.ok) {
        toast.success(response.data.message || `${selectedProducts.length} product(s) deactivated successfully!`);
        setSelectedProducts([]);
        setSelectAll(false);
        fetchProducts();
      } else {
        toast.error(response.data.detail || 'Error deactivating products');
      }
    } catch (error) {
      toast.error('Error deactivating products');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      sku: '',
      barcode: '',
      unit_price: '',
      stock_quantity: '100',
      min_stock_level: '50',
      category_id: '',
      // FBR-specific fields
      hs_code: '',
      product_description: '',
      rate: '18%',
      uom: 'Numbers, pieces, units',
      sale_type: 'Goods at standard rate (default)',
      sro_schedule: '',
      sro_item_serial_no: '',
      fixed_notified_value: '',
      sales_tax_withheld: '',
      extra_tax: '',
      further_tax: '',
      fed_payable: '',
      // FBR hybrid fields (ID + description)
      sale_type_id: '',
      sale_type_desc: '',
      rate_id: '',
      rate_value: '',
      uom_id: '',
      uom_desc: '',
      secondary_uom: '',
      conversion_factor: '',
      sro_schedule_id: '',
      sro_item_id: '',
      province_id: '',
      province_desc: '',
      origination_supplier: '' // Legacy field
    });
  };



  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const getStockStatus = (quantity, minLevel) => {
    if (quantity <= 0) return { status: 'out', color: 'text-red-600', bg: 'bg-red-100' };
    if (quantity <= minLevel) return { status: 'low', color: 'text-yellow-600', bg: 'bg-yellow-100' };
    return { status: 'good', color: 'text-green-600', bg: 'bg-green-100' };
  };

  const getStockStatusText = (quantity, minLevel) => {
    if (quantity <= 0) return 'Out of Stock';
    if (quantity <= minLevel) return 'Low Stock';
    return 'In Stock';
  };

  const renderProductModal = () => {
    if (!showModal) return null;

    return (
      <div className="fixed inset-0 bg-slate-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
        <div className="relative top-20 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-lg bg-white">
          <div className="mt-3">
            <h3 className="text-lg font-medium text-slate-900 mb-4">
              {editingProduct ? 'Edit Product' : 'Add New Product'}
            </h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Basic Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Product Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Category
                  </label>
                  <select
                    value={formData.category_id}
                    onChange={(e) => setFormData({...formData, category_id: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                  >
                    <option value="">Select Category</option>
                    {categories.map(category => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    SKU
                    <span className="text-slate-500 text-xs ml-1">(Auto-generated)</span>
                  </label>
                  <input
                    type="text"
                    value={formData.sku}
                    onChange={(e) => setFormData({...formData, sku: e.target.value})}
                    placeholder="Leave empty for auto-generation"
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Barcode
                    <span className="text-slate-500 text-xs ml-1">(Auto-generated )</span>
                  </label>
                  <input
                    type="text"
                    value={formData.barcode}
                    onChange={(e) => setFormData({...formData, barcode: e.target.value})}
                    placeholder="Leave empty for auto-generation"
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                  />
                </div>
              </div>
              
              {/* Unit Price, Stock Quantity, Minimum Stock Level in same row */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Unit Price (Rs.) *
                  </label>
                  <input
                    type="number"
                    step="0.0001"
                    min="0"
                    inputMode="decimal"
                    value={formData.unit_price}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === '' || /^\d*\.?\d{0,4}$/.test(value)) {
                        setFormData({...formData, unit_price: value});
                      }
                    }}
                    onBlur={(e) => {
                      const value = e.target.value.trim();
                      if (value && !isNaN(value) && parseFloat(value) >= 0) {
                        setFormData({...formData, unit_price: value});
                      } else if (value === '') {
                        setFormData({...formData, unit_price: ''});
                      }
                    }}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                    required
                    placeholder="0.0000"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Stock Quantity *
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={formData.stock_quantity}
                    onChange={(e) => setFormData({...formData, stock_quantity: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Minimum Stock Level
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={formData.min_stock_level}
                    onChange={(e) => setFormData({...formData, min_stock_level: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                  />
                </div>
              </div>

              {/* FBR Configuration */}
              <div className="border-t pt-4">
                <div className="flex items-center mb-4">
                  <Info className="h-5 w-5 text-amber-600 mr-2" />
                  <h4 className="text-md font-medium text-slate-900">FBR Configuration</h4>
                  {loadingFbrData && (
                    <div className="ml-2 text-sm text-slate-500">Loading FBR data...</div>
                  )}
                </div>
                <p className="text-sm text-slate-600 mb-4">
                  These fields are required for FBR (Federal Board of Revenue) compliance and invoice submission.
                </p>
                
                {/* HS Code - Full Width */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    HS Code
                  </label>
                  <SearchableDropdown
                    options={fbrData.hsCodes.map(hs => ({
                      id: hs.hs_code,
                      description: `${hs.hs_code} - ${hs.description.substring(0, 50)}...`
                    }))}
                    value={formData.hs_code}
                    onChange={handleHsCodeChange}
                    placeholder={`Select HS Code (${fbrData.hsCodes.length} available)`}
                    searchPlaceholder="Search HS codes... (client-side filtering)"
                    displayKey="description"
                    valueKey="id"
                    loading={loadingFbrData}
                    maxDisplayItems={50}
                    showMoreText="More HS codes available - use search to filter"
                  />
                </div>
                
                {/* Transaction Type, Province */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Transaction Type
                    </label>
                    <SearchableDropdown
                      options={fbrData.transactionTypes.map(type => ({
                        id: type.id,
                        description: type.description
                      }))}
                      value={formData.sale_type_id}
                      onChange={handleTransactionTypeChange}
                      placeholder={`Select Transaction Type (${fbrData.transactionTypes.length} available)`}
                      searchPlaceholder="Search transaction types..."
                      displayKey="description"
                      valueKey="id"
                      loading={loadingFbrData}
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Province
                    </label>
                    <SearchableDropdown
                      options={fbrData.provinces.map(province => ({
                        id: province.id,
                        description: province.description
                      }))}
                      value={formData.origination_supplier}
                      onChange={handleProvinceChange}
                      placeholder={`Select Province (${fbrData.provinces.length} available)`}
                      searchPlaceholder="Search provinces..."
                      displayKey="description"
                      valueKey="id"
                      loading={loadingFbrData}
                    />
                  </div>
                </div>

                {/* Unit of Measure, Tax Rate */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Unit of Measure
                    </label>
                    <SearchableDropdown
                      options={fbrData.uomList.map(uom => ({
                        id: uom.description,
                        description: uom.description
                      }))}
                      value={formData.uom}
                      onChange={(value) => {
                        const selectedUom = fbrData.uomList.find(uom => uom.description === value);
                        setFormData(prev => ({
                          ...prev,
                          uom: value,
                          uom_id: selectedUom?.id || '',
                          uom_desc: value
                        }));
                      }}
                      placeholder={`Select UOM (${fbrData.uomList.length} available)`}
                      searchPlaceholder="Search UOM..."
                      displayKey="description"
                      valueKey="id"
                      showValue={false}
                      loading={loadingFbrData}
                    />
                    <p className="text-xs text-slate-400 mt-1">Primary unit. This is what is sent to FBR.</p>
                  </div>

                  {/* Optional secondary unit (display only, never sent to FBR) */}
                  <div className="md:col-span-2 rounded-lg border border-amber-200 bg-amber-50/50 p-3">
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Secondary unit (optional)
                    </label>
                    <p className="text-xs text-slate-500 mb-2">
                      Show a second quantity on the invoice, for example weight in kg alongside length in meters.
                      FBR still only receives the primary unit.
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">Secondary UOM</label>
                        <SearchableDropdown
                          options={secondaryUomOptions}
                          value={formData.secondary_uom || ''}
                          onChange={(value) => setFormData(prev => ({
                            ...prev,
                            secondary_uom: value || '',
                            conversion_factor: value ? prev.conversion_factor : ''
                          }))}
                          onCreate={(unit) => {
                            const name = unit.trim();
                            if (!name) return;
                            setCustomUoms(prev => (
                              prev.some(u => u.toLowerCase() === name.toLowerCase()) ? prev : [...prev, name]
                            ));
                            setFormData(prev => ({ ...prev, secondary_uom: name }));
                          }}
                          createLabel="Add unit"
                          showValue={false}
                          placeholder="Select secondary unit"
                          searchPlaceholder="Search or type a new unit..."
                          displayKey="description"
                          valueKey="id"
                          loading={loadingFbrData}
                        />
                        <p className="text-xs text-slate-400 mt-1">
                          Any unit, not only the ones FBR allows for this HS code. Type a unit that is
                          not listed and add it.
                        </p>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">
                          Conversion factor {formData.secondary_uom ? '' : '(set a secondary unit first)'}
                        </label>
                        <input
                          type="number"
                          step="0.000001"
                          min="0"
                          value={formData.conversion_factor || ''}
                          onChange={(e) => setFormData(prev => ({ ...prev, conversion_factor: e.target.value }))}
                          disabled={!formData.secondary_uom}
                          placeholder="e.g. 1.5"
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 disabled:bg-slate-100"
                        />
                        {formData.secondary_uom && formData.conversion_factor ? (
                          <p className="text-xs text-slate-500 mt-1">
                            1 {formData.uom || 'primary unit'} = {formData.conversion_factor} {formData.secondary_uom}
                          </p>
                        ) : (
                          <p className="text-xs text-slate-400 mt-1">Secondary units per 1 primary unit.</p>
                        )}
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Tax Rate
                    </label>
                    <SearchableDropdown
                      options={fbrData.taxRates.map(rate => ({
                        id: rate.id,
                        description: `${rate.description} (${rate.value}%)`
                      }))}
                      value={formData.rate_id}
                      onChange={handleTaxRateChange}
                      placeholder="Select Tax Rate"
                      searchPlaceholder="Search tax rates..."
                      displayKey="description"
                      valueKey="id"
                      disabled={!fbrData.taxRates.length}
                      loading={loadingFbrData}
                    />
                  </div>
                </div>

                {/* SRO Schedule, SRO Item */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      SRO Schedule
                    </label>
                    <SearchableDropdown
                      options={fbrData.sroSchedules.map(schedule => ({
                        id: schedule.id,
                        description: schedule.description
                      }))}
                      value={formData.sro_schedule_id}
                      onChange={handleSroScheduleChange}
                      placeholder="Select SRO Schedule"
                      searchPlaceholder="Search SRO schedules..."
                      displayKey="description"
                      valueKey="id"
                      disabled={!fbrData.sroSchedules.length}
                      loading={loadingFbrData}
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      SRO Item
                    </label>
                    <SearchableDropdown
                      options={fbrData.sroItems.map(item => ({
                        id: item.id,
                        description: `${item.description} (${item.id})`
                      }))}
                      value={formData.sro_item_id}
                      onChange={(value) => {
                        const selectedItem = fbrData.sroItems.find(item => item.id === value);
                        setFormData(prev => ({
                          ...prev,
                          sro_item_serial_no: selectedItem?.description || value,
                          sro_item_id: value
                        }));
                      }}
                      placeholder={formData.sro_schedule_id ? "Select SRO Item" : "Select SRO Schedule first"}
                      searchPlaceholder="Search SRO items..."
                      displayKey="description"
                      valueKey="id"
                      disabled={!formData.sro_schedule_id || !fbrData.sroItems.length}
                      loading={loadingFbrData}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Product Description for FBR
                  </label>
                  <textarea
                    value={formData.product_description}
                    onChange={(e) => setFormData({...formData, product_description: e.target.value})}
                    rows="2"
                    placeholder="Detailed product description for FBR compliance"
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                  />
                </div>


                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Fixed Notified Value (Rs.)
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.fixed_notified_value}
                      onChange={(e) => setFormData({...formData, fixed_notified_value: e.target.value})}
                      placeholder="Fixed notified value or retail price"
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Sales Tax Withheld (Rs.)
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.sales_tax_withheld}
                      onChange={(e) => setFormData({...formData, sales_tax_withheld: e.target.value})}
                      placeholder="Sales tax withheld at source"
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Extra Tax (Rs.)
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.extra_tax}
                      onChange={(e) => setFormData({...formData, extra_tax: e.target.value})}
                      placeholder="Extra tax amount"
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Further Tax (Rs.)
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.further_tax}
                      onChange={(e) => setFormData({...formData, further_tax: e.target.value})}
                      placeholder="Further tax amount"
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Federal Tax Payable (Rs.)
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.fed_payable}
                    onChange={(e) => setFormData({...formData, fed_payable: e.target.value})}
                    placeholder="Federal tax payable amount"
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                  />
                </div>
              </div>
              
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingProduct(null);
                    resetForm();
                  }}
                  className="px-4 py-2 border border-slate-300 rounded-lg text-slate-700 hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-800 shadow-sm hover:shadow-md transition-all"
                >
                  {editingProduct ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  };

  const renderBody = () => {
    if (loading) {
      return (
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600 mx-auto"></div>
          <p className="mt-4 text-slate-600">Loading products...</p>
        </div>
      );
    }

    return (
      <div>
        {/* Header */}
        <div className="bg-white shadow">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center py-6">
              <div>
                <h1 className="text-3xl font-bold text-slate-900">Products</h1>
                <p className="text-slate-600">Manage your product inventory</p>
              </div>
              <button
                onClick={() => setShowModal(true)}
                className="bg-slate-700 hover:bg-slate-800 text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center shadow-sm hover:shadow-md transition-all"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Product
              </button>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Search and Filters */}
          <div className="bg-white rounded-lg shadow mb-6">
            <div className="p-4">
              {/* Search Bar */}
              <div className="mb-3">
                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder="Search products..."
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    onKeyPress={handleSearchKeyPress}
                    className="flex-1 px-3 py-1.5 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                  />
                  <button
                    onClick={handleSearch}
                    className="px-4 py-1.5 text-sm bg-slate-700 text-white rounded-lg hover:bg-slate-800 flex items-center gap-1"
                  >
                    <Search className="h-4 w-4" />
                    Search
                  </button>
                </div>
              </div>
              
              {/* Filters Row 1 */}
              <div className="grid grid-cols-1 md:grid-cols-5 gap-3 mb-3">
                <select
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                  className="px-2 py-1.5 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                >
                  <option value="">All Categories</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
                <select
                  value={stockFilter}
                  onChange={(e) => setStockFilter(e.target.value)}
                  className="px-2 py-1.5 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                >
                  <option value="">All Stock Levels</option>
                  <option value="in_stock">In Stock</option>
                  <option value="low_stock">Low Stock</option>
                  <option value="out_of_stock">Out of Stock</option>
                </select>
                <select
                  value={saleTypeFilter}
                  onChange={(e) => setSaleTypeFilter(e.target.value)}
                  className="px-2 py-1.5 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                >
                  <option value="">All Sale Types</option>
                  {filterValues.sale_types.map((type) => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
                <select
                  value={taxRateFilter}
                  onChange={(e) => setTaxRateFilter(e.target.value)}
                  className="px-2 py-1.5 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                >
                  <option value="">All Tax Rates</option>
                  {filterValues.tax_rates.map((rate) => (
                    <option key={rate} value={rate}>{rate}%</option>
                  ))}
                </select>
                <select
                  value={provinceFilter}
                  onChange={(e) => setProvinceFilter(e.target.value)}
                  className="px-2 py-1.5 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                >
                  <option value="">All Provinces</option>
                  {filterValues.provinces.map((province) => (
                    <option key={province} value={province}>{province}</option>
                  ))}
                </select>
              </div>
              
              {/* Filters Row 2 */}
              <div className="grid grid-cols-1 md:grid-cols-5 gap-3 mb-3">
                <select
                  value={sroScheduleFilter}
                  onChange={(e) => setSroScheduleFilter(e.target.value)}
                  className="px-2 py-1.5 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                >
                  <option value="">All SRO Schedules</option>
                  {filterValues.sro_schedules.map((schedule) => (
                    <option key={schedule} value={schedule}>{schedule}</option>
                  ))}
                </select>
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="px-2 py-1.5 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                  placeholder="From Date"
                />
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="px-2 py-1.5 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                  placeholder="To Date"
                />
                <select
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="px-2 py-1.5 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                >
                  <option value={10}>10 per page</option>
                  <option value={20}>20 per page</option>
                  <option value={50}>50 per page</option>
                  <option value={100}>100 per page</option>
                </select>
                <button
                  onClick={handleClearFilters}
                  className="px-3 py-1.5 text-sm text-slate-600 border border-slate-300 rounded-lg hover:bg-slate-50 flex items-center justify-center"
                >
                  <Filter className="h-4 w-4 mr-1" />
                  Clear Filters
                </button>
              </div>
              
              {/* Pagination and Info */}
              <div className="flex justify-between items-center pt-2 border-t border-slate-200">
                <div className="text-xs text-slate-600">
                  Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, totalProducts)} of{' '}
                  <span className="font-medium">{totalProducts}</span> products
                </div>
                <nav className="inline-flex rounded-lg shadow-sm -space-x-px">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-2 py-1 rounded-l-md border border-slate-300 bg-white text-xs font-medium text-slate-500 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <span className="relative inline-flex items-center px-3 py-1 border border-slate-300 bg-white text-xs font-medium text-slate-700">
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className="relative inline-flex items-center px-2 py-1 rounded-r-md border border-slate-300 bg-white text-xs font-medium text-slate-500 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </nav>
              </div>
            </div>
          </div>

          {/* Products Table */}
          <div className="bg-white rounded-lg shadow">
            {loading ? (
              <div className="p-8 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600 mx-auto"></div>
                <p className="mt-2 text-slate-600">Loading products...</p>
              </div>
            ) : products.length === 0 ? (
              <div className="p-8 text-center text-slate-500">
                <Package className="h-12 w-12 mx-auto mb-4 text-slate-400" />
                <p>No products found</p>
              </div>
            ) : (
              <>
                {selectedProducts.length > 0 && (
                  <div className="bg-indigo-50 border-b border-indigo-200 px-6 py-3 flex justify-between items-center">
                    <span className="text-sm font-medium text-indigo-700">
                      {selectedProducts.length} product(s) selected
                    </span>
                    <button
                      onClick={bulkDeactivateProducts}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm"
                    >
                      Deactivate Selected
                    </button>
                  </div>
                )}
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-slate-200">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-6 py-3 text-left">
                      <input
                        type="checkbox"
                        checked={selectAll}
                        onChange={handleSelectAll}
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-slate-300 rounded cursor-pointer"
                      />
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      <button
                        onClick={() => handleSort('name')}
                        className="flex items-center space-x-1 hover:text-slate-700"
                      >
                        <span>Product</span>
                        {getSortIcon('name')}
                      </button>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider w-32">
                      <button
                        onClick={() => handleSort('sku')}
                        className="flex items-center space-x-1 hover:text-slate-700"
                      >
                        <span>SKU</span>
                        {getSortIcon('sku')}
                      </button>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider" style={{minWidth: '250px', maxWidth: '300px'}}>
                      <button
                        onClick={() => handleSort('sale_type')}
                        className="flex items-center space-x-1 hover:text-slate-700"
                      >
                        <span>Sale Type</span>
                        {getSortIcon('sale_type')}
                      </button>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      <button
                        onClick={() => handleSort('unit_price')}
                        className="flex items-center space-x-1 hover:text-slate-700"
                      >
                        <span>Price</span>
                        {getSortIcon('unit_price')}
                      </button>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      <button
                        onClick={() => handleSort('stock_quantity')}
                        className="flex items-center space-x-1 hover:text-slate-700"
                      >
                        <span>Stock</span>
                        {getSortIcon('stock_quantity')}
                      </button>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      <button
                        onClick={() => handleSort('hs_code')}
                        className="flex items-center space-x-1 hover:text-slate-700"
                      >
                        <span>FBR Info</span>
                        {getSortIcon('hs_code')}
                      </button>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      <button
                        onClick={() => handleSort('created_at')}
                        className="flex items-center space-x-1 hover:text-slate-700"
                      >
                        <span>Status</span>
                        {getSortIcon('created_at')}
                      </button>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-slate-200">
                  {products.map((product) => {
                    const stockStatus = getStockStatus(product.stock_quantity, product.min_stock_level);
                    return (
                      <tr key={product.id} className="hover:bg-slate-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <input
                            type="checkbox"
                            checked={selectedProducts.includes(product.id)}
                            onChange={() => handleSelectProduct(product.id)}
                            className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-slate-300 rounded cursor-pointer"
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <Package className="h-8 w-8 text-slate-400 mr-3" />
                            <div>
                              <div className="text-sm font-medium text-slate-900">
                                {product.name}
                              </div>
                              <div className="text-sm text-slate-500">
                                {product.description}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 w-32">
                          <div className="text-sm text-slate-900 break-words">{product.sku}</div>
                        </td>
                        <td className="px-6 py-4" style={{minWidth: '250px', maxWidth: '300px'}}>
                          <div className="text-sm text-slate-900">
                            {(product.sale_type_desc || product.sale_type) && (
                              <div className="text-xs text-slate-700">{product.sale_type_desc || product.sale_type}</div>
                            )}
                            {product.sro_schedule_no && (
                              <div className="text-xs text-slate-500">{product.sro_schedule_no}</div>
                            )}
                            {product.sro_item_serial_no && (
                              <div className="text-xs text-slate-500">{product.sro_item_serial_no}</div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-slate-900">
                            Rs. {product.unit_price?.toLocaleString()}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-slate-900">
                            {product.stock_quantity}
                          </div>
                          <div className="text-sm text-slate-500">
                            Min: {product.min_stock_level}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-slate-900">
                            {product.hs_code && (
                              <div className="text-xs text-slate-500">HS: {product.hs_code}</div>
                            )}
                            {product.rate && (
                              <div className="text-xs text-slate-500">Rate: {product.rate}</div>
                            )}
                            {product.uom && (
                              <div className="text-xs text-slate-500">UOM: {product.uom}</div>
                            )}
                            {product.secondary_uom && product.conversion_factor && (
                              <div className="text-xs text-amber-700">2nd UOM: {product.secondary_uom} (×{product.conversion_factor})</div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${stockStatus.bg} ${stockStatus.color}`}>
                            {getStockStatusText(product.stock_quantity, product.min_stock_level)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex space-x-2">
                            <button
                              onClick={() => handleEdit(product)}
                              className="text-amber-600 hover:text-amber-800"
                              title="Edit Product"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDelete(product.id)}
                              className="text-red-600 hover:text-red-900"
                              title="Delete Product"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <Navigation>
      <div className="min-h-screen bg-slate-50">
        {renderBody()}
        {renderProductModal()}
      </div>
    </Navigation>
  );
};

export default Products; 